import type { DashboardBuilderState, DashboardWidgetType, DashboardWidgetInstance } from './types';

const GRID_COLS = 12;

type Tier = 'primary' | 'secondary' | 'tertiary' | 'utility';

const TIER_MAP: Record<DashboardWidgetType, Tier> = {
  web_map: 'primary',
  viz_tabs: 'primary',
  chart_bar: 'secondary',
  chart_line: 'secondary',
  chart_area: 'secondary',
  chart_donut: 'secondary',
  chart_radar: 'secondary',
  chart_scatter: 'secondary',
  chart_treemap: 'secondary',
  calendar_heatmap: 'secondary',
  data_table: 'secondary',
  device_list: 'secondary',
  activity_timeline: 'secondary',
  live_status: 'secondary',
  geofence: 'secondary',
  indicator: 'tertiary',
  gauge: 'tertiary',
  text: 'tertiary',
  placeholder: 'utility',
  iframe: 'utility',
  spatial_analytics: 'utility',
  widget_stack: 'secondary',
  widget_group: 'secondary',
};

const TIER_PRIORITY: Record<Tier, number> = {
  primary: 0,
  secondary: 1,
  tertiary: 2,
  utility: 3,
};

interface TierSizeHint {
  preferredW: number;
  minW: number;
  minH: number;
}

const TIER_SIZE: Record<Tier, TierSizeHint> = {
  primary: { preferredW: 8, minW: 4, minH: 6 },
  secondary: { preferredW: 6, minW: 3, minH: 4 },
  tertiary: { preferredW: 3, minW: 2, minH: 2 },
  utility: { preferredW: 4, minW: 2, minH: 2 },
};

interface WidgetEntry {
  id: string;
  type: DashboardWidgetType;
  tier: Tier;
  currentArea: number;
}

/**
 * Greedy bin-packing on a fixed-width grid.
 * Walks top-to-bottom, left-to-right, placing each widget in the first open slot.
 */
function binPack(
  entries: WidgetEntry[],
  totalRows: number,
): Array<{ i: string; x: number; y: number; w: number; h: number; minW: number; minH: number }> {
  const grid: boolean[][] = [];
  function ensureRows(n: number) {
    while (grid.length < n) grid.push(new Array(GRID_COLS).fill(false));
  }
  ensureRows(totalRows);

  function fits(x: number, y: number, w: number, h: number): boolean {
    ensureRows(y + h);
    for (let r = y; r < y + h; r++) {
      for (let c = x; c < x + w; c++) {
        if (c >= GRID_COLS || grid[r][c]) return false;
      }
    }
    return true;
  }

  function place(x: number, y: number, w: number, h: number) {
    ensureRows(y + h);
    for (let r = y; r < y + h; r++) {
      for (let c = x; c < x + w; c++) {
        grid[r][c] = true;
      }
    }
  }

  const results: Array<{ i: string; x: number; y: number; w: number; h: number; minW: number; minH: number }> = [];

  for (const entry of entries) {
    const size = TIER_SIZE[entry.tier];
    let w = Math.min(size.preferredW, GRID_COLS);
    const h = Math.max(size.minH, Math.round(totalRows / (entries.length <= 3 ? 1 : 2)));
    const minW = size.minW;
    const minH = size.minH;

    let placed = false;
    for (let y = 0; !placed; y++) {
      ensureRows(y + h);
      for (let x = 0; x <= GRID_COLS - w; x++) {
        if (fits(x, y, w, h)) {
          place(x, y, w, h);
          results.push({ i: entry.id, x, y, w, h, minW, minH });
          placed = true;
          break;
        }
      }
      if (!placed && w > minW) {
        w = Math.max(minW, w - 1);
      }
    }
  }

  return results;
}

/**
 * Re-layout existing widgets to fill the viewport without scroll.
 * Pure function — returns a new state.
 */
export function autoLayout(currentState: DashboardBuilderState): DashboardBuilderState {
  const entries: WidgetEntry[] = [];
  for (const item of currentState.layout) {
    const inst = currentState.widgets[item.i];
    if (!inst) continue;
    entries.push({
      id: item.i,
      type: inst.type,
      tier: TIER_MAP[inst.type] ?? 'utility',
      currentArea: item.w * item.h,
    });
  }

  entries.sort((a, b) => {
    const tp = TIER_PRIORITY[a.tier] - TIER_PRIORITY[b.tier];
    if (tp !== 0) return tp;
    return b.currentArea - a.currentArea;
  });

  const targetRows = Math.max(12, entries.length * 3);
  const layout = binPack(entries, targetRows);

  return {
    ...currentState,
    layout,
    layoutMode: 'fit',
  };
}

export interface WidgetSuggestion {
  type: DashboardWidgetType;
  reason: string;
}

/**
 * Inspect the current dashboard and suggest widgets that might be useful.
 */
export function suggestWidgets(state: DashboardBuilderState): WidgetSuggestion[] {
  const existing = new Set(Object.values(state.widgets).map((w) => w.type));
  const suggestions: WidgetSuggestion[] = [];

  const hasMap = existing.has('web_map');
  const hasChart = existing.has('chart_bar') || existing.has('chart_line') || existing.has('chart_donut')
    || existing.has('chart_area') || existing.has('chart_radar') || existing.has('chart_scatter')
    || existing.has('chart_treemap') || existing.has('viz_tabs');
  const hasTable = existing.has('data_table');
  const hasIndicator = existing.has('indicator');
  const hasGeofence = existing.has('geofence');

  if (hasMap && !hasGeofence) {
    suggestions.push({ type: 'geofence', reason: 'You have a map — add geofences to monitor spatial areas' });
  }
  if (hasMap && !hasChart) {
    suggestions.push({ type: 'chart_bar', reason: 'Visualize your map data with a chart' });
  }
  if (!hasIndicator) {
    suggestions.push({ type: 'indicator', reason: 'Add a headline metric to track key numbers' });
  }
  if (hasChart && !hasTable) {
    suggestions.push({ type: 'data_table', reason: 'See the underlying records in a table' });
  }
  if (!hasMap) {
    suggestions.push({ type: 'web_map', reason: 'Add a map to display spatial data' });
  }

  return suggestions;
}

/**
 * Apply accepted suggestions to a state — adds the widgets and re-runs auto-layout.
 */
export function applySuggestions(
  state: DashboardBuilderState,
  types: DashboardWidgetType[],
): DashboardBuilderState {
  let nextState = { ...state, widgets: { ...state.widgets }, layout: [...state.layout] };
  let idCounter = Object.keys(nextState.widgets).length + 1;

  for (const type of types) {
    const id = `auto_${idCounter++}`;
    const inst = buildDefaultInstance(type);
    const y = nextState.layout.reduce((max, item) => Math.max(max, item.y + item.h), 0);
    const tier = TIER_MAP[type] ?? 'utility';
    const size = TIER_SIZE[tier];
    nextState.layout.push({
      i: id,
      x: 0,
      y,
      w: size.preferredW,
      h: size.minH,
      minW: size.minW,
      minH: size.minH,
    });
    nextState.widgets[id] = inst;
  }

  return autoLayout(nextState);
}

function buildDefaultInstance(type: DashboardWidgetType): DashboardWidgetInstance {
  switch (type) {
    case 'web_map':
      return { type, config: { webMapOptions: { layers: [], basemap: 'dark' } } };
    case 'geofence':
      return { type, config: { geofences: [], basemap: 'dark' } };
    case 'indicator':
      return { type, config: { customTitle: 'New Metric' } };
    case 'data_table':
      return { type, config: { customTitle: 'Data Table' } };
    case 'text':
      return { type, config: { title: 'New Text', body: '' } };
    default:
      return { type, config: { customTitle: type.replace(/_/g, ' ') } };
  }
}
